import styled, {
  boxShadows,
  BoxShadows,
  breakpoints,
  Button,
  css,
  Device,
  FlattenSimpleInterpolation,
  IFalconTheme,
  ResponsiveTypography,
  Scale,
  StyledComponent,
  TextInput,
  Tooltip,
} from '@se-admin/falcon';
import { Color } from '../../common/types/color';

export {
  BookingAmendmentTextInput,
  BookingAmendmentTextInputWithMargin,
  Container,
  infoContainerStyles,
  TextInputContainer,
  NoteContainer,
  TitleContainer,
  StyledTooltip,
  StyledTooltipContent,
  StyledTooltipTitle,
  CalendarInfoContainer,
  CalendarInfoContent,
  PriceBreakdownSection,
  CalendarContainer,
  LineItemContainer,
  StyledButton,
};

const INPUT_SECTION_MIN_WIDTH: number = 400;
const CALENDAR_INFO_CONTENT_WIDTH: number = 52;
const CALENDAR_INFO_CONTAINER_ZINDEX: number = 10000;

const infoContainerStyles: FlattenSimpleInterpolation = css`
  padding: ${Scale.m};
  box-shadow: ${boxShadows[BoxShadows.level1]};
  position: fixed;
  box-sizing: border-box;
  bottom: 0;
  width: 100%;
  background: ${Color.white};

  @media (min-width: ${breakpoints[Device.tablet]}) {
    display: flex;
    box-shadow: none;
    position: static;
    flex-direction: row;
    justify-content: flex-end;
  }
`;

const BookingAmendmentTextInputWithMargin: StyledComponent<typeof TextInput, {}, {}> = styled(
  TextInput
)`
  margin-right: ${Scale.m};
  input {
    outline: none;
  }
  @media (min-width: ${breakpoints[Device.tablet]}) {
    margin-right: ${Scale.l};
  }
`;

const BookingAmendmentTextInput: StyledComponent<typeof TextInput, {}, {}> = styled(TextInput)`
  input {
    outline: none;
  }
`;

const TextInputContainer: StyledComponent<'div', IFalconTheme> = styled('div')(
  ({ theme }: { theme: IFalconTheme }) => {
    return css`
      background: ${theme.color.grey05};
      padding: ${Scale.m};
      display: flex;
      z-index: 1;
      position: relative;

      @media (min-width: ${breakpoints[Device.tablet]}) {
        background: ${Color.white};
        width: 100%;
        min-width: ${INPUT_SECTION_MIN_WIDTH}px;
        box-sizing: border-box;
      }
    `;
  }
);

const LineItemContainer: StyledComponent<'div', {}, {}> = styled('div')`
  margin: ${Scale.xxs} 0;
`;

const NoteContainer: StyledComponent<'div', IFalconTheme, {}> = styled('div')(
  ({ theme }: { theme: IFalconTheme }) => {
    return css`
      display: flex;
      flex-direction: column;
      background: ${theme.color.warning05};
      border-left: ${Scale.xxxs} solid ${theme.color.warning30};
      padding: ${Scale.xs};
      margin-bottom: ${Scale.m};
    `;
  }
);

const Container: StyledComponent<'div', {}, {}> = styled('div')`
  width: 100%;
  background: ${Color.white};
  height: 100vh;

  @media (min-width: ${breakpoints[Device.tablet]}) {
    height: 100%;
  }
`;

const StyledButton: StyledComponent<typeof Button, {}, {}> = styled(Button)`
  margin-right: ${Scale.s};

  @media (min-width: ${breakpoints[Device.desktop]}) {
    margin-right: ${Scale.l};
  }
`;

const TitleContainer: StyledComponent<typeof ResponsiveTypography, {}> = styled(
  ResponsiveTypography
)`
  padding: ${Scale.m};
`;

const StyledTooltip: StyledComponent<typeof Tooltip, IFalconTheme, {}> = styled(Tooltip)`
  padding-left: ${Scale.xxs};
  width: ${Scale.m};
  height: ${Scale.m};
`;

const StyledTooltipContent: StyledComponent<'div', IFalconTheme, {}> = styled('div')`
  @media (min-width: ${breakpoints[Device.tablet]}) {
    display: flex;
  }

  ul,
  li {
    list-style: inside;
  }
`;

const StyledTooltipTitle: StyledComponent<typeof ResponsiveTypography, IFalconTheme, {}> = styled(
  ResponsiveTypography
)`
  margin-bottom: ${Scale.xs};
`;

const CalendarContainer: StyledComponent<'div', {}, {}> = styled('div')`
  body .DayPicker {
    position: relative;
  }
`;

const CalendarInfoContainer: StyledComponent<'div', {}, {}> = styled('div')`
  border-top: 1px solid grey;
  align-self: flex-end;
  z-index: ${CALENDAR_INFO_CONTAINER_ZINDEX};
  @media (min-width: ${breakpoints[Device.tablet]}) {
    z-index: auto;
  }
  ${infoContainerStyles}
`;

const CalendarInfoContent: StyledComponent<'div', {}, {}> = styled('div')`
  @media (min-width: ${breakpoints[Device.tablet]}) {
    width: ${CALENDAR_INFO_CONTENT_WIDTH}%;
  }
`;

const PriceBreakdownSection: StyledComponent<'div', {}, {}> = styled('div')`
  padding-bottom: ${Scale.m};
`;
